import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  GraduationCap, Users, Layers, Shield, School, BookOpen, Database, Trophy, ClipboardCheck,
  ScrollText, User, KeyRound, Crown, ArrowRight, Sparkles,
} from 'lucide-react';

// Rutas REALES tomadas de menus.super_admin / menus.assistant_admin (AppLayout).
// No se crean rutas ni permisos nuevos. "Academia" es sólo agrupación visual:
// Alumnos/Profesores/Administradores navegan a /admin/users (página con pestañas
// internas students/teachers/admins); Grupos a /admin/groups.
const ACADEMIA_BASE = [
  { key: 'alumnos',    to: '/admin/users',  label: 'Alumnos',        desc: 'Gestiona a los estudiantes', icon: GraduationCap, tile: 'bg-emerald-100 text-emerald-700', glow: 'group-hover:shadow-emerald-200/60' },
  { key: 'profesores', to: '/admin/users',  label: 'Profesores',     desc: 'Gestiona a los docentes',    icon: Users,         tile: 'bg-blue-100 text-blue-700',       glow: 'group-hover:shadow-blue-200/60' },
  { key: 'grupos',     to: '/admin/groups', label: 'Grupos',         desc: 'Clases y agrupaciones',      icon: Layers,        tile: 'bg-amber-100 text-amber-700',     glow: 'group-hover:shadow-amber-200/60' },
];
const ACADEMIA_ADMINS = { key: 'admins', to: '/admin/users', label: 'Administradores', desc: 'Equipo administrativo', icon: Shield, tile: 'bg-rose-100 text-rose-700', glow: 'group-hover:shadow-rose-200/60' };

const TOOLS_FULL = [
  { key: 'salones',   to: '/admin/classrooms',    label: 'Salones',       desc: 'Clases en vivo',        icon: School,      tile: 'bg-amber-100 text-amber-700',   glow: 'group-hover:shadow-amber-200/60' },
  { key: 'cursos',    to: '/admin/courses',       label: 'Cursos',        desc: 'Catálogo de cursos',    icon: BookOpen,    tile: 'bg-blue-100 text-blue-700',     glow: 'group-hover:shadow-blue-200/60' },
  { key: 'estudios',  to: '/admin/studies',       label: 'Estudios',      desc: 'Estudios de la academia', icon: GraduationCap, tile: 'bg-indigo-100 text-indigo-700', glow: 'group-hover:shadow-indigo-200/60' },
  { key: 'database',  to: '/admin/database',      label: 'Base de datos', desc: 'Partidas y posiciones', icon: Database,    tile: 'bg-slate-200 text-slate-700',   glow: 'group-hover:shadow-slate-300/60' },
  { key: 'logros',    to: '/admin/achievements',  label: 'Logros',        desc: 'Insignias y trofeos',   icon: Trophy,      tile: 'bg-amber-100 text-amber-700',   glow: 'group-hover:shadow-amber-200/60' },
  { key: 'auditoria', to: '/admin/audit',         label: 'Auditoría',     desc: 'Registro de actividad', icon: ScrollText,  tile: 'bg-violet-100 text-violet-700', glow: 'group-hover:shadow-violet-200/60' },
  { key: 'perfil',    to: '/admin/profile',       label: 'Mi perfil',     desc: 'Tu cuenta',             icon: User,        tile: 'bg-blue-100 text-blue-700',     glow: 'group-hover:shadow-blue-200/60' },
  { key: 'accesos',   to: '/admin/login-history', label: 'Accesos',       desc: 'Historial de ingresos', icon: KeyRound,    tile: 'bg-slate-200 text-slate-700',   glow: 'group-hover:shadow-slate-300/60' },
];
// assistant_admin: operación = Alumnos, Grupos y Seguimiento.
const TOOLS_ASSISTANT = [
  { key: 'alumnos',     to: '/admin/users',  label: 'Alumnos',     desc: 'Crear y gestionar alumnos', icon: GraduationCap, tile: 'bg-emerald-100 text-emerald-700', glow: 'group-hover:shadow-emerald-200/60' },
  { key: 'grupos',      to: '/admin/groups', label: 'Grupos',      desc: 'Crear y asignar alumnos',   icon: Layers,        tile: 'bg-amber-100 text-amber-700',     glow: 'group-hover:shadow-amber-200/60' },
  { key: 'seguimiento', to: '/admin/tasks',   label: 'Seguimiento', desc: 'Graduaciones y atención',   icon: ClipboardCheck, tile: 'bg-blue-100 text-blue-700',    glow: 'group-hover:shadow-blue-200/60' },
];

const ROLE_LABEL = {
  super_admin: 'Super administrador', school_admin: 'Administrador de escuela',
  partner_admin: 'Partner Admin', assistant_admin: 'Administrador asistente',
};

function ToolCard({ item, i, mounted, prefix }) {
  const Icon = item.icon;
  const style = { transitionDelay: `${i * 40}ms`, opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(12px)' };
  return (
    <Link
      to={item.to}
      data-testid={`${prefix}-${item.key}`}
      style={style}
      className={`group relative flex flex-col rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl ${item.glow} hover:border-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400`}
    >
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl grid place-items-center mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-105 ${item.tile}`}>
        <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
      </div>
      <div className="font-display font-bold text-base sm:text-lg text-slate-800 leading-tight">{item.label}</div>
      <div className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-snug">{item.desc}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 group-hover:text-amber-600 transition-colors">
        Abrir <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    api.get('/stats/overview').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const isAssistant = user?.role === 'assistant_admin';
  const academia = isAssistant ? TOOLS_ASSISTANT : [...ACADEMIA_BASE, ACADEMIA_ADMINS];
  const tools = isAssistant ? [] : TOOLS_FULL;

  const summary = [
    { label: 'Alumnos', value: stats.students },
    { label: 'Profesores', value: stats.teachers },
    { label: 'Cursos', value: stats.courses },
    { label: 'Partidas', value: stats.games },
  ];

  return (
    <div className="min-h-full bg-[#F5F7FB]" data-testid="admin-dashboard">
      {/* HEADER — identidad Chess4Kings (navy + ámbar) */}
      <div className="relative overflow-hidden bg-[#002537] text-white">
        {!user?.is_owner && <div className="absolute -right-16 -top-24 w-80 h-80 rounded-full bg-amber-500/20 blur-3xl" />}
        {!user?.is_owner && <div className="absolute -left-20 -bottom-24 w-72 h-72 rounded-full bg-blue-500/20 blur-3xl" />}
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-5 pb-5 sm:pt-6 sm:pb-7">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur grid place-items-center ring-1 ring-white/15">
              <Crown className="w-5 h-5 text-amber-300" />
            </div>
            <span className="font-display font-extrabold tracking-tight">Chess4Kings</span>
            {user?.is_owner && (
              <span data-testid="owner-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white text-[9px] font-extrabold uppercase tracking-widest">
                <Shield className="w-2.5 h-2.5" /> Owner
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="admin-greeting">
            Centro de administración
          </h1>
          <p className="text-white/70 mt-2 text-sm sm:text-base">
            Hola, {user?.first_name || 'admin'} · {ROLE_LABEL[user?.role] || 'Administrador'}. Gestiona tu academia.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-6 sm:-mt-8 pb-12 relative">
        {/* 1) ACADEMIA / OPERACIÓN — protagonista */}
        <div className="mb-3 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-amber-500" />
          <h2 className="font-display font-bold text-lg text-white">{isAssistant ? 'Operación' : 'Academia'}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="admin-academia">
          {academia.map((it, i) => <ToolCard key={it.key} item={it} i={i} mounted={mounted} prefix="admin-academia" />)}
        </div>

        {/* 2) HERRAMIENTAS / PLATAFORMA — sólo administradores */}
        {!isAssistant && (
          <>
            <div className="mt-8 mb-3 flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              <h2 className="font-display font-bold text-lg text-slate-800">Plataforma</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="admin-tools">
              {tools.map((it, i) => <ToolCard key={it.key} item={it} i={i} mounted={mounted} prefix="admin-tool" />)}
            </div>
          </>
        )}

        {/* 3) RESUMEN — sólo administradores */}
        {!isAssistant && (
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-4 sm:p-5" data-testid="admin-summary">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Resumen de la academia</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {summary.map(s => (
                <div key={s.label} className="rounded-xl bg-slate-50 px-3 py-2.5">
                  <div className="font-display text-2xl font-extrabold text-slate-800">{s.value ?? 0}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
